import { useEffect, useRef, useState } from 'react';
import { supabase } from '../../shared/supabase/supabaseClient';
import { resetNetworkCallAudit } from '../../shared/dev/networkCallAudit';
import { markBoot, resetBootTiming } from '../../shared/dev/bootTiming';
import { captureCatch } from '../../shared/sentry/sentryUser';
import { useAuthStore } from '../store/useAuthStore';
import { logout } from '../application/auth';

export function useAuthSessionHydration(): boolean {
  const setSession = useAuthStore((state) => state.setSession);
  const [initialAuthReady, setInitialAuthReady] = useState(false);
  const authHydratedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      if (session) {
        if (event === 'INITIAL_SESSION' && authHydratedRef.current) {
          setInitialAuthReady(true);
          return;
        }
        setSession(session, session.user ?? null);
        if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN' || event === 'PASSWORD_RECOVERY') {
          authHydratedRef.current = true;
        }
        setInitialAuthReady(true);
        return;
      }
      if (event === 'SIGNED_OUT') {
        authHydratedRef.current = false;
        resetNetworkCallAudit();
        resetBootTiming();
        setInitialAuthReady(true);
        logout();
      }
    });

    markBoot('auth_get_session_start');
    supabase.auth.getSession()
      .then(async ({ data: { session } }) => {
        if (cancelled) return;
        if (!session) return;

        const expiresAtMs = (session.expires_at ?? 0) * 1000;
        if (expiresAtMs > Date.now()) {
          setSession(session, session.user ?? null);
          authHydratedRef.current = true;
          return;
        }

        // Access token expirado → intentar refresh explícito.
        markBoot('auth_refresh_session');
        const { data: refreshed, error } = await supabase.auth.refreshSession();
        if (cancelled) return;
        if (error || !refreshed.session) {
          void supabase.auth.signOut({ scope: 'local' }).catch(captureCatch('auth', 'hydration-signout-refresh-failed'));
          return;
        }
        setSession(refreshed.session, refreshed.session.user ?? null);
        authHydratedRef.current = true;
      })
      .catch((error) => {
        // Token inválido/expirado al arrancar → limpiar storage local.
        captureCatch('auth', 'hydration-get-session')(error);
        void supabase.auth.signOut({ scope: 'local' }).catch(captureCatch('auth', 'hydration-signout-catch'));
      })
      .finally(() => {
        if (!cancelled) {
          setInitialAuthReady(true);
          markBoot('auth_ready');
        }
      });

    return () => {
      cancelled = true;
      listener.subscription.unsubscribe();
    };
  }, [setSession]);

  return initialAuthReady;
}
